/**
 * @fileoverview Builds the localized thinking-level label map shared wherever a model's
 * thinking level is shown read-only (the agent details summary and the version diff
 * drawer). Centralizes the labels so the map isn't copy/pasted between call sites, mirroring
 * how {@link formatLogicalModelLabel} centralizes the model label itself.
 * @owner @Appboy/intelligence
 */

import type { I18nT } from "@lib/dashboardI18n";

import type { ThinkingLevel } from "./types";

/**
 * Returns the translated label for every {@link ThinkingLevel}, keyed by thinking level
 * (e.g. `{ high: "High" }`). The `Record<ThinkingLevel, string>` return type makes the map
 * exhaustive: adding a new thinking level to shared_constants.json causes a compile-time
 * error here until its label is supplied.
 *
 * Pass the result to {@link formatLogicalModelLabel}'s `thinkingLevelLabels` option, or use
 * it directly to label a thinking level.
 */
export const thinkingLevelLabelsGetter = (t: I18nT<"agent-details">): Record<ThinkingLevel, string> => ({
  none: t("thinking-level-none-label", "None"),
  minimal: t("thinking-level-minimal-label", "Minimal"),
  low: t("thinking-level-low-label", "Low"),
  medium: t("thinking-level-medium-label", "Medium"),
  high: t("thinking-level-high-label", "High"),
});
